import React, { useState, useMemo } from 'react';
import { X, Pizza, Plus, Search, Loader2 } from 'lucide-react';

export default function ToppingModal({ toppingItems = [], loading = false, onAdd, onClose }) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredToppings = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return toppingItems;
    return toppingItems.filter((item) => item.name.toLowerCase().includes(query));
  }, [toppingItems, searchQuery]);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col transform animate-slideUp">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-t-2xl p-6 flex-shrink-0">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X size={20} />
          </button>

          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
              <Pizza size={24} />
            </div>
            <div>
              <h3 className="text-xl font-bold">Add Topping</h3>
              <p className="text-sm text-white/80">Select a topping to add to this order</p>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="px-6 pt-4 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search toppings..."
              className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              autoFocus
            />
          </div>
        </div>

        {/* Toppings List */}
        <div className="p-6 pt-3 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-10 text-slate-400">
              <Loader2 size={28} className="animate-spin mb-2" />
              <p className="text-sm">Loading toppings...</p>
            </div>
          ) : filteredToppings.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-slate-400">
              <Pizza size={32} className="mb-2 text-slate-300" />
              <p className="text-sm font-medium">
                {toppingItems.length === 0 ? 'No items marked as toppings yet' : 'No toppings match your search'}
              </p>
              {toppingItems.length === 0 && (
                <p className="text-xs text-slate-400 mt-1">
                  Mark items as "Topping" in Menu Management → Options tab
                </p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2.5">
              {filteredToppings.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onAdd(item)}
                  className="flex items-center justify-between gap-2 px-3 py-2.5 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 hover:border-teal-300 transition-colors text-left"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-teal-900 truncate">{item.name}</p>
                    <p className="text-xs text-teal-600">₹{item.price ?? item.basePrice ?? 0}</p>
                  </div>
                  <span className="flex-shrink-0 p-1 bg-teal-600 text-white rounded-full">
                    <Plus size={14} />
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 p-4 bg-slate-50 rounded-b-2xl flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 bg-white border-2 border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold rounded-lg transition-colors"
          >
            Done
          </button>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}